import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronRight, ChevronDown, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Configuration
const COUNTRY_OPTIONS = [10, 20, 40, 60, 80, 100];
const ENTITY_OPTIONS = [1, 3, 5, 10, 20];

// Calculation engine
const calculateTrueCost = (countries, entitiesPerCountry) => {
  const entities = countries * entitiesPerCountry;
  const vendors = Math.round(countries * 0.8);
  const systems = Math.round(vendors * 0.7);
  
  // Filings
  const filings = {
    vatReturns: entities * 12,
    ecSalesLists: Math.round(entities * 12 * 0.6),
    intrastat: Math.round(entities * 12 * 0.5),
    saftReporting: Math.round(entities * 12 * 0.5),
    localAccounting: entities * 12,
    localTaxFilings: entities * 6,
  };
  const totalFilings = Object.values(filings).reduce((a, b) => a + b, 0);
  
  // Costs (enterprise-grade assumptions)
  const costs = {
    vendorFees: vendors * 28000,
    internalLabor: totalFilings * 3.2 * 85,
    systemOverhead: systems * 42000,
    penaltyRisk: Math.round(totalFilings * 0.03) * 5500,
    hiddenOverhead: (vendors * 55 + entities * 30) * 85,
  };
  const totalCost = Object.values(costs).reduce((a, b) => a + b, 0);
  
  // Metrics
  const ftes = Math.round((totalFilings * 3.2) / 1800 * 10) / 10;
  const monthlyBurn = Math.round(totalCost / 12);
  const dailyBurn = Math.round(totalCost / 365);
  const costPerCountry = Math.round(totalCost / countries);
  const errors = Math.round(totalFilings * 0.03);
  const waste = Math.round(totalCost * 0.77);
  
  return {
    countries,
    entities,
    vendors,
    systems,
    totalFilings,
    costs,
    totalCost,
    ftes,
    monthlyBurn,
    dailyBurn,
    costPerCountry,
    errors,
    waste,
  };
};

// Animated counter
const AnimatedCurrency = ({ value, duration = 1000, prefix = '€' }) => {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);
  
  useEffect(() => {
    const start = prev.current;
    const end = value;
    const startTime = performance.now();
    
    const animate = (time) => {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(start + (end - start) * eased));
      if (progress < 1) requestAnimationFrame(animate);
      else prev.current = end;
    };
    requestAnimationFrame(animate);
  }, [value, duration]);
  
  const formatted = display >= 1000000 
    ? `${(display / 1000000).toFixed(1)}M`
    : display >= 1000 
      ? `${Math.round(display / 1000)}K`
      : display.toLocaleString();
  
  return <span>{prefix}{formatted}</span>;
};

const AnimatedNumber = ({ value, duration = 800 }) => {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);
  
  useEffect(() => {
    const start = prev.current;
    const end = value;
    const startTime = performance.now();
    
    const animate = (time) => {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(start + (end - start) * eased));
      if (progress < 1) requestAnimationFrame(animate);
      else prev.current = end;
    };
    requestAnimationFrame(animate);
  }, [value, duration]);
  
  return <span>{display.toLocaleString()}</span>;
};

// Phase components
const CONTEXT_TAGS = [
  'e-Invoicing', 'SAF-T', 'VAT Returns', 'EC Sales Lists',
  'eReporting', 'Intrastat', 'CTC Mandates',
];

const HERO_TOC_ITEMS = [
  { id: 'client-logos', label: 'Trusted By' },
  { id: 'e2e-promise', label: 'How It Works' },
  { id: 'sector-capabilities', label: 'Industries' },
  { id: 'testimonials', label: 'What Clients Say' },
  { id: 'insource', label: 'In-House Compliance' },
  { id: 'why-taxera', label: 'Why Taxera' },
];

const PhaseHook = ({ onStart }) => {
  const { t } = useTranslation();
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="text-center max-w-2xl mx-auto"
  >
    <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-taxera-charcoal leading-tight mb-6">
      {t('calc.heroTitle')}{' '}
      <span className="taxera-gradient-text">{t('calc.heroTitleHighlight')}</span> {t('calc.heroTitleEnd')}
    </h1>
    <p className="text-xl text-slate-600 mb-5">
      {t('calc.heroSubtitle')}
    </p>
    <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="hero-context-tags">
      {CONTEXT_TAGS.map((tag, i) => (
        <motion.span
          key={tag}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 + i * 0.06, duration: 0.35 }}
          className="px-3 py-1 text-xs font-medium text-slate-500 bg-slate-100 rounded-full border border-slate-200"
          data-testid={`context-tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {tag}
        </motion.span>
      ))}
    </div>
    <button
      onClick={onStart}
      className="group inline-flex items-center gap-3 px-10 py-5 taxera-gradient text-white rounded-full font-semibold text-lg hover:shadow-2xl transition-all hover:scale-105"
      data-testid="start-calculator"
    >
      {t('calc.heroCta')}
      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
    </button>

    {/* Section navigation dots */}
    <div className="flex items-center justify-center gap-4 mt-8" data-testid="hero-section-dots">
      {[
        { path: '/', label: 'Home', color: '#1e293b' },
        { path: '/platform', label: 'Platform', color: '#0d9488' },
        { path: '/products', label: 'Solutions', color: '#06b6d4' },
        { path: '/sap-s4hana', label: 'SAP S/4HANA', color: '#f59e0b' },
        { path: '/resources/videos', label: 'Resources', color: '#059669' },
        { path: '/evaluate', label: 'Evaluate', color: '#7c3aed' },
        { path: '/about', label: 'About', color: '#2563eb' },
      ].map((section, i) => (
        <motion.a
          key={section.path}
          href={section.path}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6 + i * 0.07, type: 'spring', stiffness: 400 }}
          className="group relative w-[15.5px] h-[15.5px] rounded-full transition-all duration-200 hover:scale-150 cursor-pointer border-2 bg-white"
          style={{
            borderColor: section.color,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = section.color;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
          data-testid={`hero-dot-${section.label.toLowerCase().replace(/[\s/]/g, '-')}`}
          aria-label={section.label}
        >
          <span className="absolute -top-9 left-1/2 -translate-x-1/2 px-2.5 py-1 bg-slate-900 text-white text-[10px] font-semibold rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            {section.label}
          </span>
        </motion.a>
      ))}
    </div>
  </motion.div>
  );
};

const PhaseInputs = ({ step, countries, entities, onCountrySelect, onEntitySelect, onReveal }) => {
  const { t } = useTranslation();
  return (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="max-w-xl mx-auto"
  >
    <div className="bg-white rounded-3xl border border-slate-200 shadow-xl p-8">
      {/* Progress */}
      <div className="flex items-center gap-2 mb-8">
        <div className={`h-1.5 flex-1 rounded-full ${step >= 1 ? 'bg-taxera-blue' : 'bg-slate-200'}`} />
        <div className={`h-1.5 flex-1 rounded-full ${step >= 2 ? 'bg-taxera-blue' : 'bg-slate-200'}`} />
      </div>
      
      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <h2 className="font-heading text-2xl font-bold text-taxera-charcoal mb-2">
              {t('calc.step1Title')}
            </h2>
            <p className="text-slate-500 mb-6">{t('calc.step1Subtitle')}</p>
            
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-8">
              {COUNTRY_OPTIONS.map((num) => (
                <button
                  key={num}
                  onClick={() => onCountrySelect(num)}
                  className={`py-4 rounded-xl font-bold text-lg transition-all ${
                    countries === num
                      ? 'bg-taxera-blue text-white shadow-lg scale-105'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  data-testid={`country-option-${num}`}
                >
                  {num === 100 ? '100+' : num}
                </button>
              ))}
            </div>
            
            {countries && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={() => onCountrySelect(countries)}
                className="w-full py-4 bg-taxera-charcoal text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-taxera-charcoal/90 transition-all"
              >
                Next: Legal entities
                <ChevronRight className="w-5 h-5" />
              </motion.button>
            )}
          </motion.div>
        )}
        
        {step === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <h2 className="font-heading text-2xl font-bold text-taxera-charcoal mb-2">
              {t('calc.step2Title')}
            </h2>
            <p className="text-slate-500 mb-6">{t('calc.step2Subtitle')}</p>
            
            <div className="grid grid-cols-5 gap-3 mb-6">
              {ENTITY_OPTIONS.map((num) => (
                <button
                  key={num}
                  onClick={() => onEntitySelect(num)}
                  className={`py-4 rounded-xl font-bold text-lg transition-all ${
                    entities === num
                      ? 'bg-taxera-blue text-white shadow-lg scale-105'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  data-testid={`entity-option-${num}`}
                >
                  {num}
                </button>
              ))}
            </div>
            
            {entities && (
              <div className="p-4 bg-slate-50 rounded-xl mb-6">
                <p className="text-slate-600 text-center">
                  That's <span className="font-bold text-taxera-charcoal">{countries * entities} legal entities</span> filing across{' '}
                  <span className="font-bold text-taxera-charcoal">{countries} jurisdictions</span>
                </p>
              </div>
            )}
            
            {entities && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={onReveal}
                className="w-full py-4 taxera-gradient text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg transition-all"
                data-testid="reveal-costs"
              >
                {t('calc.revealCosts')}
                <ArrowRight className="w-5 h-5" />
              </motion.button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  </motion.div>
  );
};

const PhaseReveal = ({ data, onContinue }) => {
  const { t } = useTranslation();
  const [revealStep, setRevealStep] = useState(0);
  
  useEffect(() => {
    const timers = [
      setTimeout(() => setRevealStep(1), 400),
      setTimeout(() => setRevealStep(2), 1200),
      setTimeout(() => setRevealStep(3), 2000),
      setTimeout(() => setRevealStep(4), 2800),
      setTimeout(() => setRevealStep(5), 4000),
      setTimeout(() => setRevealStep(6), 5500),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const costEntries = [
    data.costs.vendorFees,
    data.costs.internalLabor,
    data.costs.systemOverhead,
    data.costs.penaltyRisk,
  ];
  const runningTotal = costEntries.slice(0, Math.min(revealStep, 4)).reduce((a, b) => a + b, 0);

  const formatM = (v) => v >= 1000000 
    ? `€${(v / 1000000).toFixed(1)}M` 
    : `€${Math.round(v / 1000)}K`;

  const costCards = [
    {
      title: t('calc.vendorFees'),
      amount: data.costs.vendorFees,
      punch: t('calc.vendorPunch', { count: data.vendors }),
      color: 'from-cyan-500 to-blue-600',
      border: 'border-cyan-400',
      glow: 'shadow-cyan-500/20',
    },
    {
      title: t('calc.internalLabor'),
      amount: data.costs.internalLabor,
      punch: t('calc.laborPunch', { count: data.ftes }),
      color: 'from-violet-500 to-purple-600',
      border: 'border-violet-400',
      glow: 'shadow-violet-500/20',
    },
    {
      title: t('calc.systemOverhead'),
      amount: data.costs.systemOverhead,
      punch: t('calc.systemPunch', { count: data.systems }),
      color: 'from-orange-500 to-rose-600',
      border: 'border-orange-400',
      glow: 'shadow-orange-500/20',
    },
    {
      title: t('calc.penaltyExposure'),
      amount: data.costs.penaltyRisk,
      punch: t('calc.penaltyPunch', { count: data.errors }),
      color: 'from-rose-500 to-red-600',
      border: 'border-rose-400',
      glow: 'shadow-rose-500/20',
    },
  ];
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-6xl mx-auto"
    >
      {/* Top row: 4 cost cards + running total */}
      <div className="grid lg:grid-cols-12 gap-6 mb-6">
        <div className="lg:col-span-7">
          <div className="grid grid-cols-2 gap-3">
            {costCards.map((card, i) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, scale: 0.85, y: 20 }}
                animate={{
                  opacity: revealStep > i ? 1 : 0,
                  scale: revealStep > i ? 1 : 0.85,
                  y: revealStep > i ? 0 : 20,
                }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className={`relative overflow-hidden rounded-2xl border ${card.border} bg-slate-900 p-5 shadow-lg ${card.glow}`}
              >
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${card.color}`} />
                <div className="text-2xs font-bold tracking-[0.2em] text-slate-500 uppercase mb-2">{card.title}</div>
                <div className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
                  <AnimatedCurrency value={revealStep > i ? card.amount : 0} duration={700} />
                </div>
                <p className="text-sm text-slate-400 leading-snug">{card.punch}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: revealStep >= 1 ? 1 : 0, x: revealStep >= 1 ? 0 : 30 }}
            transition={{ duration: 0.5 }}
            className="bg-slate-900 rounded-2xl p-6 md:p-8 border border-slate-800 text-center"
          >
            <div className="text-xs font-bold tracking-[0.25em] text-slate-500 uppercase mb-1">{t('calc.runningTotal')}</div>
            <div className="text-xs text-slate-600 mb-4">
              {revealStep >= 4 ? t('calc.allRevealed') : t('calc.of4', { count: Math.min(revealStep, 4) })}
            </div>
            <div className={`font-heading text-5xl md:text-6xl font-bold mb-4 transition-colors duration-700 ${
              revealStep >= 4 ? 'text-red-400' : revealStep >= 3 ? 'text-orange-400' : revealStep >= 2 ? 'text-violet-400' : 'text-cyan-400'
            }`}>
              <AnimatedCurrency value={runningTotal} duration={600} />
            </div>
            <div className="h-3 bg-slate-800 rounded-full overflow-hidden mb-4">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-cyan-400 via-violet-500 via-orange-500 to-red-500"
                initial={{ width: '0%' }}
                animate={{ width: `${Math.min(revealStep, 4) * 25}%` }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
            <div className="flex justify-center gap-2">
              {['bg-cyan-400', 'bg-violet-500', 'bg-orange-500', 'bg-red-500'].map((color, i) => (
                <div
                  key={`step-${color}`}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    revealStep > i ? color : 'bg-slate-700'
                  }`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Total slam with truth bar on the right + CTA at top */}
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{
          opacity: revealStep >= 5 ? 1 : 0,
          scale: revealStep >= 5 ? 1 : 0.94,
        }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative rounded-3xl overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute inset-0 animate-pulse-glow opacity-30 rounded-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] bg-red-500/15 rounded-full blur-[120px]" />

        <div className="relative z-10 px-8 py-8 md:py-10">
          {/* CTA at the top right */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: revealStep >= 6 ? 1 : 0 }}
            transition={{ delay: 0.5 }}
            className="flex justify-end mb-6"
          >
            <button
              onClick={onContinue}
              className="group inline-flex items-center gap-2 px-6 py-3 bg-white text-taxera-charcoal rounded-full font-semibold hover:shadow-2xl hover:scale-105 transition-all"
              data-testid="see-alternative"
            >
              {t('calc.seeAlternative')}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>

          <div className="grid lg:grid-cols-12 gap-8 items-center">
            {/* Left: Total + metrics */}
            <div className="lg:col-span-7">
              <div className="text-xs font-bold tracking-[0.3em] text-red-400 uppercase mb-2">
                {t('calc.totalAnnualCost')}
              </div>
              <div className="font-heading text-7xl sm:text-8xl md:text-9xl font-bold text-white tracking-tight mb-3">
                {formatM(data.totalCost)}
              </div>
              <div className="text-slate-500 mb-4">{t('calc.perYear')}</div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: revealStep >= 5 ? 1 : 0 }}
                transition={{ delay: 0.8 }}
                className="flex flex-wrap gap-3"
              >
                <div className="bg-white/5 rounded-lg px-4 py-2 border border-white/10">
                  <span className="text-lg font-bold text-white">{formatM(data.monthlyBurn)}</span>
                  <span className="text-slate-500 text-sm ml-1">{t('calc.perMonth')}</span>
                </div>
                <div className="bg-white/5 rounded-lg px-4 py-2 border border-white/10">
                  <span className="text-lg font-bold text-white">€{data.dailyBurn.toLocaleString()}</span>
                  <span className="text-slate-500 text-sm ml-1">{t('calc.perDay')}</span>
                </div>
                <div className="bg-white/5 rounded-lg px-4 py-2 border border-white/10">
                  <span className="text-lg font-bold text-white">{Math.round(data.ftes)}</span>
                  <span className="text-slate-500 text-sm ml-1">{t('calc.ftesBuried')}</span>
                </div>
              </motion.div>
            </div>

            {/* Right: Truth bar */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: revealStep >= 6 ? 1 : 0, y: revealStep >= 6 ? 0 : 15 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-5"
            >
              <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
                <div className="h-3 bg-slate-800 rounded-full overflow-hidden mb-3">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: revealStep >= 6 ? '77%' : 0 }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    className="h-full rounded-full bg-gradient-to-r from-taxera-teal to-red-500"
                  />
                </div>
                <div className="flex justify-between text-xs mb-3">
                  <span className="text-taxera-teal font-bold">{t('calc.realWork')}</span>
                  <span className="text-red-400 font-bold">{t('calc.waste')}</span>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed mb-3">
                  For every <span className="font-bold text-white">€1</span> you spend, <span className="font-bold text-red-400">€0.77 goes to fragmentation</span> — not compliance.
                </p>
                <div className="text-center pt-3 border-t border-white/10">
                  <div className="font-heading text-2xl font-bold text-red-400">{formatM(data.waste)}</div>
                  <div className="text-slate-500 text-xs">burned on chaos per year</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── PHASE 4: SHOW ME HOW (Gamified Journey) ──
const PhaseShowMeHow = ({ data, onContinue }) => {
  const { t } = useTranslation();
  const [journeyStep, setJourneyStep] = useState(1);
  const [vendorChoice, setVendorChoice] = useState(null);
  const [unlockedSavings, setUnlockedSavings] = useState(new Set());
  const [showRecovery, setShowRecovery] = useState(false);

  const formatM = (v) => v >= 1000000
    ? `€${(v / 1000000).toFixed(1)}M`
    : `€${Math.round(v / 1000)}K`;

  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [journeyStep]);

  const VENDOR_OPTIONS = [1, 5, 10, 15, 20];

  const handleVendorConfirm = () => {
    if (vendorChoice !== null) {
      setTimeout(() => setJourneyStep(2), 300);
    }
  };

  // STEP 2: Savings unlock
  const savingsCategories = [
    {
      id: 'vendors',
      title: t('calc.vendorConsolidation'),
      current: data.costs.vendorFees,
      withTaxera: Math.round(data.costs.vendorFees * 0.15),
      color: 'from-cyan-500 to-blue-600',
      border: 'border-cyan-400',
      desc: t('calc.vendorDesc', { from: data.vendors }),
    },
    {
      id: 'labor',
      title: t('calc.laborAutomation'),
      current: data.costs.internalLabor,
      withTaxera: Math.round(data.costs.internalLabor * 0.20),
      color: 'from-violet-500 to-purple-600',
      border: 'border-violet-400',
      desc: t('calc.laborDesc', { from: data.ftes, to: Math.round(data.ftes * 0.25) }),
    },
    {
      id: 'integration',
      title: t('calc.e2eIntegration'),
      current: data.costs.systemOverhead,
      withTaxera: Math.round(data.costs.systemOverhead * 0.10),
      color: 'from-orange-500 to-rose-600',
      border: 'border-orange-400',
      desc: t('calc.integrationDesc'),
    },
    {
      id: 'penalties',
      title: t('calc.errorElimination'),
      current: data.costs.penaltyRisk,
      withTaxera: Math.round(data.costs.penaltyRisk * 0.05),
      color: 'from-rose-500 to-red-600',
      border: 'border-rose-400',
      desc: t('calc.errorDesc', { count: data.errors }),
    },
  ];

  const totalSavings = savingsCategories
    .filter(c => unlockedSavings.has(c.id))
    .reduce((sum, c) => sum + (c.current - c.withTaxera), 0);

  const totalPossibleSavings = savingsCategories
    .reduce((sum, c) => sum + (c.current - c.withTaxera), 0);

  const handleUnlock = (id) => {
    const next = new Set(unlockedSavings);
    next.add(id);
    setUnlockedSavings(next);
  };

  // STEP 3: Recovery reveal
  useEffect(() => {
    if (journeyStep === 3) {
      setTimeout(() => setShowRecovery(true), 400);
    }
  }, [journeyStep]);

  const threeYearSavings = totalPossibleSavings * 3;
  const roi = Math.round((totalPossibleSavings / data.totalCost) * 100);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-5xl mx-auto"
    >
      {/* Journey Progress */}
      <div className="flex items-center justify-center gap-2 mb-8">
        {[
          { n: 1, label: t('calc.consolidate') },
          { n: 2, label: t('calc.unlock') },
          { n: 3, label: t('calc.recover') },
        ].map(({ n, label }) => (
          <div key={n} className="flex items-center gap-2">
            <div className="flex flex-col items-center gap-1">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-500 ${
                journeyStep >= n
                  ? 'bg-gradient-to-r from-taxera-teal to-taxera-blue text-white shadow-md'
                  : 'bg-slate-200 text-slate-400'
              }`}>
                {n}
              </div>
              <span className={`text-xs font-medium transition-colors ${journeyStep >= n ? 'text-taxera-teal' : 'text-slate-400'}`}>
                {label}
              </span>
            </div>
            {n < 3 && (
              <div className={`w-16 h-0.5 mb-5 transition-all duration-700 ${
                journeyStep > n ? 'bg-taxera-teal' : 'bg-slate-200'
              }`} />
            )}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* ── STEP 1: CONSOLIDATE VENDORS ── */}
        {journeyStep === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="max-w-xl mx-auto"
          >
            <div className="bg-white rounded-3xl border border-slate-200 shadow-xl p-8">
              <h2 className="font-heading text-2xl font-bold text-taxera-charcoal mb-2">
                You currently rely on {data.vendors} vendors
              </h2>
              <p className="text-slate-500 mb-6">
                How many would you <span className="font-semibold text-taxera-charcoal">want</span> to manage?
              </p>

              <div className="grid grid-cols-5 gap-3 mb-6">
                {VENDOR_OPTIONS.map((num) => (
                  <button
                    key={num}
                    onClick={() => setVendorChoice(num)}
                    className={`py-3 rounded-xl font-bold text-base transition-all ${
                      vendorChoice === num
                        ? 'bg-taxera-blue text-white shadow-lg scale-105'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                    data-testid={`vendor-option-${num}`}
                  >
                    {num}
                  </button>
                ))}
              </div>

              {vendorChoice !== null && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {/* Visual: Current → Target */}
                  <div className="flex items-center justify-center gap-4 mb-6 p-4 bg-slate-50 rounded-xl">
                    <div className="text-center">
                      <div className="flex flex-wrap gap-1 justify-center max-w-[100px] mb-2">
                        {Array.from({ length: Math.min(data.vendors, 16) }).map((_, i) => (
                          <div key={i} className="w-2.5 h-2.5 bg-slate-300 rounded-sm" />
                        ))}
                      </div>
                      <span className="text-xs text-slate-500">{t('calc.today', { count: data.vendors })}</span>
                    </div>
                    <ArrowRight className="w-5 h-5 text-taxera-teal shrink-0" />
                    <div className="text-center">
                      <button
                        onClick={() => { setVendorChoice(1); }}
                        className={`w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-2 transition-all cursor-pointer ${
                          vendorChoice === 1
                            ? 'bg-gradient-to-br from-taxera-teal to-taxera-blue shadow-lg scale-110'
                            : 'bg-slate-200 hover:bg-gradient-to-br hover:from-taxera-teal hover:to-taxera-blue hover:scale-105'
                        }`}
                        data-testid="vendor-option-taxera"
                      >
                        <span className={`font-bold text-sm ${vendorChoice === 1 ? 'text-white' : 'text-slate-500 hover:text-white'}`}>1</span>
                      </button>
                      <span className="text-xs text-taxera-teal font-medium">{t('calc.allInOne').split(' ').slice(0, -2).join(' ')}<br />{t('calc.allInOne').split(' ').slice(-2).join(' ')}</span>
                    </div>
                  </div>

                  {/* Dual CTA */}
                  <div className="space-y-2">
                    {vendorChoice !== 1 && (
                      <button
                        onClick={() => { setVendorChoice(1); handleVendorConfirm(); }}
                        className="w-full py-3 bg-white border-2 border-taxera-teal text-taxera-teal rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-taxera-teal/5 transition-all"
                        data-testid="consolidate-taxera"
                      >
                        {t('calc.allInOne')}
                      </button>
                    )}
                    <button
                      onClick={handleVendorConfirm}
                      className="w-full py-4 taxera-gradient text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg transition-all"
                      data-testid="consolidate-all"
                    >
                      {vendorChoice === 1 ? t('calc.allInOne') : t('calc.consolidateTo', { count: vendorChoice })}
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}

        {/* ── STEP 2: UNLOCK SAVINGS ── */}
        {journeyStep === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <div className="text-center mb-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-taxera-charcoal mb-2">
                {t('calc.unlockTitle')}
              </h2>
              <p className="text-slate-500">
                {t('calc.unlockSubtitle')}
              </p>
            </div>

            {/* Running savings counter + CTA when all unlocked */}
            <motion.div className="bg-slate-900 rounded-2xl px-6 py-4 mb-6 flex items-center justify-between">
              <div className="text-sm text-slate-400">
                <span className="font-bold text-white">{unlockedSavings.size}</span> {t('calc.unlocked', { count: '' }).replace(' ', '')}
              </div>
              {unlockedSavings.size >= 4 ? (
                <button
                  onClick={() => setJourneyStep(3)}
                  className="group inline-flex items-center gap-2 px-6 py-2.5 taxera-gradient text-white rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all"
                  data-testid="view-recovery"
                >
                  {t('calc.viewBusinessCase')}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              ) : (
                <div className="font-heading text-3xl md:text-4xl font-bold text-emerald-400">
                  +<AnimatedCurrency value={totalSavings} duration={600} prefix="" />
                  <span className="text-lg text-slate-500 ml-1">{t('calc.saved')}</span>
                </div>
              )}
            </motion.div>

            {/* Savings tiles */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {savingsCategories.map((cat) => {
                const isUnlocked = unlockedSavings.has(cat.id);
                const saving = cat.current - cat.withTaxera;
                return (
                  <motion.button
                    key={cat.id}
                    onClick={() => !isUnlocked && handleUnlock(cat.id)}
                    whileHover={!isUnlocked ? { scale: 1.02 } : {}}
                    whileTap={!isUnlocked ? { scale: 0.98 } : {}}
                    className={`relative overflow-hidden rounded-2xl p-6 text-left transition-all duration-500 ${
                      isUnlocked
                        ? `bg-slate-900 border ${cat.border}`
                        : 'bg-slate-100 border-2 border-dashed border-slate-300 hover:border-taxera-teal cursor-pointer'
                    }`}
                    data-testid={`unlock-${cat.id}`}
                  >
                    {isUnlocked && (
                      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${cat.color}`} />
                    )}

                    <div className={`text-xs font-bold tracking-[0.15em] uppercase mb-3 ${
                      isUnlocked ? 'text-slate-400' : 'text-slate-500'
                    }`}>
                      {cat.title}
                    </div>

                    {isUnlocked ? (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <div className="flex items-baseline gap-3 mb-2">
                          <span className="text-slate-500 line-through text-lg">{formatM(cat.current)}</span>
                          <span className="text-white font-heading text-2xl font-bold">{formatM(cat.withTaxera)}</span>
                        </div>
                        <div className="text-emerald-400 font-bold text-lg mb-1">
                          +{formatM(saving)} saved
                        </div>
                        <div className="text-slate-500 text-sm">{cat.desc}</div>
                      </motion.div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-slate-200 flex items-center justify-center">
                          <span className="text-slate-400 text-lg">?</span>
                        </div>
                        <span className="text-slate-400 font-medium">{t('calc.clickToUnlock')}</span>
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* ── STEP 3: YOUR RECOVERY ── */}
        {journeyStep === 3 && (
          <motion.div
            key="step3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="text-center"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-taxera-charcoal mb-6">
              {t('calc.recoveryTitle')}
            </h2>

            {/* Recovery cards */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: showRecovery ? 1 : 0, y: showRecovery ? 0 : 20 }}
                transition={{ delay: 0 }}
                className="bg-slate-900 rounded-2xl p-6 border border-emerald-500/30"
              >
                <div className="text-xs font-bold tracking-[0.2em] text-emerald-400 uppercase mb-2">{t('calc.annualSavings')}</div>
                <div className="font-heading text-4xl md:text-5xl font-bold text-white mb-1">
                  {formatM(totalPossibleSavings)}
                </div>
                <div className="text-slate-500 text-sm">{t('calc.recoveredPerYear')}</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: showRecovery ? 1 : 0, y: showRecovery ? 0 : 20 }}
                transition={{ delay: 0.2 }}
                className="bg-slate-900 rounded-2xl p-6 border border-cyan-500/30"
              >
                <div className="text-xs font-bold tracking-[0.2em] text-cyan-400 uppercase mb-2">{t('calc.threeYearImpact')}</div>
                <div className="font-heading text-4xl md:text-5xl font-bold text-white mb-1">
                  {formatM(threeYearSavings)}
                </div>
                <div className="text-slate-500 text-sm">{t('calc.threeYearSavings')}</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: showRecovery ? 1 : 0, y: showRecovery ? 0 : 20 }}
                transition={{ delay: 0.4 }}
                className="bg-slate-900 rounded-2xl p-6 border border-violet-500/30"
              >
                <div className="text-xs font-bold tracking-[0.2em] text-violet-400 uppercase mb-2">ROI</div>
                <div className="font-heading text-4xl md:text-5xl font-bold text-white mb-1">
                  {roi}%
                </div>
                <div className="text-slate-500 text-sm">{t('calc.costReduction')}</div>
              </motion.div>
            </div>

            {/* Provocation */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: showRecovery ? 1 : 0 }}
              transition={{ delay: 0.8 }}
              className="mb-8"
            >
              <p className="text-lg text-slate-600 max-w-xl mx-auto">
                You're operating across <span className="font-bold text-taxera-charcoal">{data.countries} countries</span> with{' '}
                <span className="font-bold text-taxera-charcoal">{data.entities} entities</span>.{' '}
                {vendorChoice === 1 ? (
                  <>Taxera handles all of it — one integration, one platform, one invoice.</>
                ) : (
                  <>Consolidated to <span className="font-bold text-taxera-teal">{vendorChoice} vendors</span> including Taxera — fewer integrations, less overhead, more control.</>
                )}
              </p>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: showRecovery ? 1 : 0, y: showRecovery ? 0 : 10 }}
              transition={{ delay: 1 }}
            >
              <button
                onClick={onContinue}
                className="group inline-flex items-center gap-3 px-10 py-5 taxera-gradient text-white rounded-2xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all"
                data-testid="get-roadmap"
              >
                {t('calc.getRoadmap')}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ── PHASE 5: LEAD CAPTURE ──
const PhaseEngage = ({ data }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [status, setStatus] = useState('idle');

  const formatM = (v) => v >= 1000000
    ? `€${(v / 1000000).toFixed(1)}M`
    : `€${Math.round(v / 1000)}K`;

  const totalPossibleSavings = 
    (data.costs.vendorFees * 0.85) +
    (data.costs.internalLabor * 0.80) +
    (data.costs.systemOverhead * 0.90) +
    (data.costs.penaltyRisk * 0.95);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      await axios.post(`${API}/api/leads/newsletter`, {
        email,
        company,
        source: 'cost_calculator_roadmap',
        metadata: {
          countries: data.countries,
          entities: data.entities,
          totalCost: data.totalCost,
          savings: totalPossibleSavings,
        },
      });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto"
    >
      <div className="bg-slate-900 rounded-3xl p-8 md:p-10 text-center relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[200px] bg-taxera-teal/10 rounded-full blur-[100px]" />

        <div className="relative z-10">
          <div className="text-xs font-bold tracking-[0.25em] text-taxera-teal uppercase mb-3">
            {t('calc.roadmapLabel')}
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
            {t('calc.inAnnualSavings', { amount: formatM(totalPossibleSavings) })}
          </h2>
          <p className="text-slate-400 mb-8 max-w-md mx-auto">
            {t('calc.roadmapSubtitle', { countries: data.countries, entities: data.entities })}
          </p>

          {status === 'success' ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-6"
            >
              <div className="text-emerald-400 font-heading text-xl font-bold mb-2">
                {t('calc.roadmapSent')}
              </div>
              <p className="text-slate-400 text-sm">
                {t('calc.roadmapSentSubtitle')}
              </p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('calc.workEmail')}
                required
                className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-taxera-teal/40 focus:border-taxera-teal/40"
                data-testid="capture-email"
              />
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder={t('calc.companyName')}
                className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-taxera-teal/40 focus:border-taxera-teal/40"
                data-testid="capture-company"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full px-6 py-4 taxera-gradient text-white rounded-xl font-semibold text-lg hover:shadow-lg transition-all disabled:opacity-70"
                data-testid="capture-submit"
              >
                {status === 'loading' ? t('calc.sending') : t('calc.sendRoadmap')}
              </button>
              {status === 'error' && (
                <p className="text-red-400 text-sm">{t('calc.submitError')}</p>
              )}
            </form>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Main Component
const TrueCostCalculator = ({ onPhaseChange }) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState('hook'); // hook, inputs, reveal, showme, engage
  const [step, setStep] = useState(1);
  const [countries, setCountries] = useState(null);
  const [entities, setEntities] = useState(null);
  const [data, setData] = useState(null);
  
  const handleStart = () => setPhase('inputs');

  // Notify parent of phase changes
  useEffect(() => {
    onPhaseChange?.(phase);
  }, [phase, onPhaseChange]);
  
  const handleCountrySelect = (num) => {
    setCountries(num);
    if (countries === num) setStep(2);
  };
  
  const handleEntitySelect = (num) => {
    setEntities(num);
  };
  
  const handleReveal = () => {
    const calculated = calculateTrueCost(countries, entities);
    setData(calculated);
    setPhase('reveal');
  };
  
  const handleReset = () => {
    setPhase('hook');
    setStep(1);
    setCountries(null);
    setEntities(null);
    setData(null);
  };
  
  return (
    <section 
      className="min-h-screen pt-28 pb-16 flex items-center relative overflow-hidden"
      data-testid="true-cost-calculator"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-slate-50" />
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-taxera-mint/5 to-transparent" />
      
      <div className="relative z-10 max-w-6xl mx-auto px-6 w-full">
        {/* Reset button (when not on hook) */}
        {phase !== 'hook' && (
          <div className="flex justify-end mb-4">
            <button
              onClick={handleReset}
              className="text-slate-400 hover:text-taxera-blue flex items-center gap-1 text-sm transition-colors"
              data-testid="reset-btn"
            >
              <RotateCcw className="w-4 h-4" />
              {t('calc.startOver')}
            </button>
          </div>
        )}
        
        <AnimatePresence mode="wait">
          {phase === 'hook' && (
            <motion.div key="hook" exit={{ opacity: 0, y: -20 }}>
              <PhaseHook onStart={handleStart} />
            </motion.div>
          )}
          
          {phase === 'inputs' && (
            <motion.div key="inputs" exit={{ opacity: 0, y: -20 }}>
              <PhaseInputs
                step={step}
                countries={countries}
                entities={entities}
                onCountrySelect={handleCountrySelect}
                onEntitySelect={handleEntitySelect}
                onReveal={handleReveal}
              />
            </motion.div>
          )}
          
          {phase === 'reveal' && data && (
            <motion.div key="reveal" exit={{ opacity: 0, y: -20 }}>
              <PhaseReveal data={data} onContinue={() => setPhase('showme')} />
            </motion.div>
          )}
          
          {phase === 'showme' && data && (
            <motion.div key="showme" exit={{ opacity: 0, y: -20 }}>
              <PhaseShowMeHow data={data} onContinue={() => setPhase('engage')} />
            </motion.div>
          )}
          
          {phase === 'engage' && data && (
            <motion.div key="engage">
              <PhaseEngage data={data} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default TrueCostCalculator;
